import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from "react";
import { themeApi } from "@/lib/api";
import { BusinessType, getBusinessConfig } from "@/lib/businessConfig";

// =====================================================
// THEME TYPES
// =====================================================
export interface ThemeColors {
  background: string;
  foreground: string;
  card: string;
  "card-foreground": string;
  primary: string;
  "primary-foreground": string;
  secondary: string;
  "secondary-foreground": string;
  muted: string;
  "muted-foreground": string;
  accent: string;
  "accent-foreground": string;
  destructive: string;
  "destructive-foreground": string;
  border: string;
  input: string;
  ring: string;
  gold: string;
  "gold-light": string;
  "gold-dark": string;
  charcoal: string;
  "charcoal-light": string;
  beige: string;
  "beige-dark": string;
}

export interface ThemeTypography {
  headingFont: string;
  bodyFont: string;
  headingWeight: string;
  bodyWeight: string;
  baseFontSize: string;
}

export interface ThemeSpacing {
  borderRadius: string;
  sectionPadding: string;
  containerMaxWidth: string;
}

export interface SectionConfig {
  id: string;
  name: string;
  visible: boolean;
  order: number;
  type?: 'built-in' | 'custom';
  blockType?: 'hero' | 'content' | 'cta' | 'cards' | 'text' | 'image-text' | 'gallery' | 'testimonial' | 'faq' | 'banner' | 'spacer';
  pageId?: string;
}

export interface SectionContent {
  heading?: string;
  subheading?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonText2?: string;
  buttonLink2?: string;
  phoneNumber?: string;
  imageUrl?: string;
  imageUrl2?: string;
  videoUrl?: string;
  mapEmbedUrl?: string;
  stats?: Array<{ value: string; label: string }>;
  items?: Array<{ title: string; description: string; icon?: string; imageUrl?: string }>;
}

export interface PageContent {
  [sectionId: string]: SectionContent;
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  businessType: BusinessType;
  category: string;
  customSections?: { [pageId: string]: SectionConfig[] };
  colors: ThemeColors;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  sections: SectionConfig[];
  animationStyle: "elegant" | "bold" | "minimal" | "playful" | "dramatic";
  pageContent: { [pageId: string]: PageContent };
}

export interface ThemeState {
  activePresetId: string;
  draft: ThemePreset;
  published: ThemePreset;
  presets: ThemePreset[];
  isDirty: boolean;
}

// =====================================================
// THEME PRESETS — each tied to a unique BusinessType
// =====================================================

const brokerConfig = getBusinessConfig("broker");
const builderConfig = getBusinessConfig("builder");
const ecoConfig = getBusinessConfig("eco");
const luxuryConfig = getBusinessConfig("luxury");
const urbanConfig = getBusinessConfig("urban");


export const LUXURY_GOLD_THEME: ThemePreset = {
  id: "luxury-gold",
  name: "Luxury Gold",
  description: "Elegant gold & charcoal — For real estate brokers",
  businessType: "broker",
  category: "broker",
  animationStyle: "elegant",
  colors: {
    background: "40 20% 98%", foreground: "220 20% 15%", card: "0 0% 100%", "card-foreground": "220 20% 15%",
    primary: "220 25% 18%", "primary-foreground": "40 20% 98%", secondary: "35 25% 95%", "secondary-foreground": "220 20% 15%",
    muted: "35 15% 93%", "muted-foreground": "220 10% 45%", accent: "42 76% 55%", "accent-foreground": "220 25% 12%",
    destructive: "0 84% 60%", "destructive-foreground": "0 0% 100%", border: "35 20% 88%", input: "35 20% 88%",
    ring: "42 76% 55%", gold: "42 76% 55%", "gold-light": "42 70% 70%", "gold-dark": "42 80% 40%",
    charcoal: "220 25% 18%", "charcoal-light": "220 15% 30%", beige: "35 25% 95%", "beige-dark": "35 20% 88%",
  },
  typography: { headingFont: "Playfair Display", bodyFont: "DM Sans", headingWeight: "600", bodyWeight: "400", baseFontSize: "16px" },
  spacing: { borderRadius: "0.5rem", sectionPadding: "4rem", containerMaxWidth: "1400px" },
  sections: [...brokerConfig.defaultSections],
  pageContent: JSON.parse(JSON.stringify(brokerConfig.defaultPageContent)),
};

export const BUILDER_BOLD_THEME: ThemePreset = {
  id: "builder-bold",
  name: "Builder Bold",
  description: "Strong navy & orange — For builders & developers",
  businessType: "builder",
  category: "builder",
  animationStyle: "bold",
  colors: {
    background: "210 15% 97%", foreground: "215 30% 12%", card: "0 0% 100%", "card-foreground": "215 30% 12%",
    primary: "215 60% 22%", "primary-foreground": "0 0% 100%", secondary: "210 10% 94%", "secondary-foreground": "215 30% 12%",
    muted: "210 10% 92%", "muted-foreground": "215 10% 42%", accent: "24 95% 53%", "accent-foreground": "0 0% 100%",
    destructive: "0 84% 60%", "destructive-foreground": "0 0% 100%", border: "210 12% 87%", input: "210 12% 87%",
    ring: "24 95% 53%", gold: "24 95% 53%", "gold-light": "24 85% 68%", "gold-dark": "24 100% 42%",
    charcoal: "215 60% 22%", "charcoal-light": "215 40% 32%", beige: "210 10% 94%", "beige-dark": "210 10% 87%",
  },
  typography: { headingFont: "Montserrat", bodyFont: "Source Sans 3", headingWeight: "700", bodyWeight: "400", baseFontSize: "16px" },
  spacing: { borderRadius: "0.25rem", sectionPadding: "4rem", containerMaxWidth: "1400px" },
  sections: [...builderConfig.defaultSections],
  pageContent: JSON.parse(JSON.stringify(builderConfig.defaultPageContent)),
};

export const GREEN_LIVING_THEME: ThemePreset = {
  id: "green-living",
  name: "Green Living",
  description: "Earthy greens & cream — For eco-friendly builders & townships",
  businessType: "eco",
  category: "eco",
  animationStyle: "minimal",
  colors: {
    background: "60 20% 97%", foreground: "150 20% 12%", card: "60 15% 99%", "card-foreground": "150 20% 12%",
    primary: "150 35% 25%", "primary-foreground": "60 20% 97%", secondary: "80 15% 93%", "secondary-foreground": "150 20% 12%",
    muted: "80 10% 91%", "muted-foreground": "150 10% 42%", accent: "85 55% 45%", "accent-foreground": "0 0% 100%",
    destructive: "0 84% 60%", "destructive-foreground": "0 0% 100%", border: "80 15% 86%", input: "80 15% 86%",
    ring: "85 55% 45%", gold: "85 55% 45%", "gold-light": "85 45% 62%", "gold-dark": "85 60% 35%",
    charcoal: "150 35% 25%", "charcoal-light": "150 25% 35%", beige: "60 20% 94%", "beige-dark": "60 15% 86%",
  },
  typography: { headingFont: "Lora", bodyFont: "Nunito", headingWeight: "600", bodyWeight: "400", baseFontSize: "16px" },
  spacing: { borderRadius: "0.75rem", sectionPadding: "5rem", containerMaxWidth: "1400px" },
  sections: [...ecoConfig.defaultSections],
  pageContent: JSON.parse(JSON.stringify(ecoConfig.defaultPageContent)),
};

export const ROYAL_ESTATE_THEME: ThemePreset = {
  id: "royal-estate",
  name: "Royal Estate",
  description: "Deep burgundy & platinum — Ultra-luxury brokers & penthouses",
  businessType: "luxury",
  category: "luxury",
  animationStyle: "dramatic",
  colors: {
    background: "0 5% 97%", foreground: "340 20% 12%", card: "0 0% 100%", "card-foreground": "340 20% 12%",
    primary: "340 45% 18%", "primary-foreground": "0 5% 97%", secondary: "350 10% 94%", "secondary-foreground": "340 20% 12%",
    muted: "350 8% 91%", "muted-foreground": "340 10% 42%", accent: "340 60% 40%", "accent-foreground": "0 0% 100%",
    destructive: "0 84% 60%", "destructive-foreground": "0 0% 100%", border: "350 10% 87%", input: "350 10% 87%",
    ring: "340 60% 40%", gold: "340 60% 40%", "gold-light": "340 50% 58%", "gold-dark": "340 65% 30%",
    charcoal: "340 45% 18%", "charcoal-light": "340 30% 28%", beige: "350 10% 94%", "beige-dark": "350 8% 87%",
  },
  typography: { headingFont: "Cormorant Garamond", bodyFont: "Raleway", headingWeight: "600", bodyWeight: "400", baseFontSize: "16px" },
  spacing: { borderRadius: "0rem", sectionPadding: "5rem", containerMaxWidth: "1400px" },
  sections: [...luxuryConfig.defaultSections],
  pageContent: JSON.parse(JSON.stringify(luxuryConfig.defaultPageContent)),
};

export const URBAN_MODERN_THEME: ThemePreset = {
  id: "urban-modern",
  name: "Urban Modern",
  description: "Slate & teal — City apartments & co-living spaces",
  businessType: "urban",
  category: "modern",
  animationStyle: "playful",
  colors: {
    background: "200 10% 97%", foreground: "200 20% 12%", card: "0 0% 100%", "card-foreground": "200 20% 12%",
    primary: "200 30% 20%", "primary-foreground": "0 0% 100%", secondary: "195 12% 94%", "secondary-foreground": "200 20% 12%",
    muted: "195 8% 91%", "muted-foreground": "200 10% 42%", accent: "175 65% 42%", "accent-foreground": "0 0% 100%",
    destructive: "0 84% 60%", "destructive-foreground": "0 0% 100%", border: "195 12% 87%", input: "195 12% 87%",
    ring: "175 65% 42%", gold: "175 65% 42%", "gold-light": "175 55% 58%", "gold-dark": "175 70% 32%",
    charcoal: "200 30% 20%", "charcoal-light": "200 20% 30%", beige: "195 12% 94%", "beige-dark": "195 8% 87%",
  },
  typography: { headingFont: "Raleway", bodyFont: "Open Sans", headingWeight: "700", bodyWeight: "400", baseFontSize: "16px" },
  spacing: { borderRadius: "1rem", sectionPadding: "4rem", containerMaxWidth: "1400px" },
  sections: [...urbanConfig.defaultSections],
  pageContent: JSON.parse(JSON.stringify(urbanConfig.defaultPageContent)),
};

const ALL_PRESETS = [LUXURY_GOLD_THEME, BUILDER_BOLD_THEME, GREEN_LIVING_THEME, ROYAL_ESTATE_THEME, URBAN_MODERN_THEME];

// =====================================================
// CONTEXT
// =====================================================
interface ThemeContextType {
  themeState: ThemeState;
  updateDraftColors: (colors: Partial<ThemeColors>) => void;
  updateDraftTypography: (typography: Partial<ThemeTypography>) => void;
  updateDraftSpacing: (spacing: Partial<ThemeSpacing>) => void;
  updateDraftSections: (sections: SectionConfig[]) => void;
  updateDraftPageContent: (pageId: string, sectionId: string, content: Partial<SectionContent>) => void;
  updateDraftCustomSections: (pageId: string, sections: SectionConfig[]) => void;
  addCustomSection: (pageId: string, blockType: SectionConfig['blockType'], name: string) => string;
  removeCustomSection: (pageId: string, sectionId: string) => void;
  switchPreset: (presetId: string) => void;
  publishDraft: () => Promise<{ savedToServer: boolean; error?: string }>;
  discardDraft: () => void;
  resetToDefault: (presetId: string) => void;
  getPublishedSections: () => SectionConfig[];
  getPublishedPageContent: (pageId: string, sectionId: string) => SectionContent | undefined;
  getDraftPageContent: (pageId: string, sectionId: string) => SectionContent | undefined;
  isEditorPreview: boolean;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

function getDefaultThemeState(): ThemeState {
  return {
    activePresetId: "luxury-gold",
    draft: { ...LUXURY_GOLD_THEME },
    published: { ...LUXURY_GOLD_THEME },
    presets: ALL_PRESETS,
    isDirty: false,
  };
}

function mapApiThemeToPreset(apiTheme: any): ThemePreset | null {
  try {
    const matchingPreset = ALL_PRESETS.find(p => p.id === apiTheme.preset_id);
    return {
      id: apiTheme.preset_id,
      name: apiTheme.name,
      description: apiTheme.description || matchingPreset?.description || '',
      businessType: (apiTheme.business_type || matchingPreset?.businessType || 'broker') as BusinessType,
      category: apiTheme.category || matchingPreset?.category || 'broker',
      animationStyle: (apiTheme.animation_style || matchingPreset?.animationStyle || 'elegant') as any,
      colors: apiTheme.colors || matchingPreset?.colors || LUXURY_GOLD_THEME.colors,
      typography: apiTheme.typography || matchingPreset?.typography || LUXURY_GOLD_THEME.typography,
      spacing: apiTheme.spacing || matchingPreset?.spacing || LUXURY_GOLD_THEME.spacing,
      sections: apiTheme.sections || matchingPreset?.sections || LUXURY_GOLD_THEME.sections,
      pageContent: apiTheme.page_content || matchingPreset?.pageContent || {},
      customSections: apiTheme.custom_sections || matchingPreset?.customSections || undefined,
    };
  } catch { return null; }
}

function applyThemeToDOM(preset: ThemePreset) {
  const root = document.documentElement;
  Object.entries(preset.colors).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
  root.style.setProperty("--radius", preset.spacing.borderRadius);

  const headingRule = `${preset.typography.headingFont}, Georgia, serif`;
  const bodyRule = `${preset.typography.bodyFont}, system-ui, sans-serif`;
  root.style.setProperty("--font-heading", headingRule);
  root.style.setProperty("--font-body", bodyRule);
  document.body.style.fontFamily = bodyRule;

  const fontsToLoad = [preset.typography.headingFont, preset.typography.bodyFont]
    .filter((f, i, arr) => arr.indexOf(f) === i)
    .map((f) => f.replace(/ /g, "+"))
    .join("&family=");

  const existingLink = document.getElementById("theme-fonts");
  if (existingLink) existingLink.remove();

  const link = document.createElement("link");
  link.id = "theme-fonts";
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontsToLoad}:wght@300;400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeState, setThemeState] = useState<ThemeState>(getDefaultThemeState);
  const [previewOverride, setPreviewOverride] = useState<ThemePreset | null>(null);
  const [isEditorPreview, setIsEditorPreview] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("_editor_preview") === "true") {
      setIsEditorPreview(true);
    }
  }, []);

  // Listen for draft theme updates from parent editor window
  useEffect(() => {
    if (!isEditorPreview) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === "THEME_DRAFT_UPDATE" && event.data.draft) {
        setPreviewOverride(event.data.draft);
        applyThemeToDOM(event.data.draft);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [isEditorPreview]);

  useEffect(() => { if (!isEditorPreview) applyThemeToDOM(themeState.published); }, [isEditorPreview]);

  // Fetch active theme from API on mount — API is the ONLY source of truth
  const apiLoaded = useRef(false);
  useEffect(() => {
    if (isEditorPreview || apiLoaded.current) return;
    apiLoaded.current = true;
    (async () => {
      try {
        const res = await themeApi.getActive();
        if (res.success && res.data) {
          const preset = mapApiThemeToPreset(res.data);
          if (preset) {
            setThemeState(prev => {
              const newState = { ...prev, published: preset, draft: preset, activePresetId: preset.id, isDirty: false };
              applyThemeToDOM(preset);
              return newState;
            });
          }
        }
        // If API returns no active theme (404), keep built-in defaults as initial render
        // This is NOT localStorage — it's the hardcoded preset used only until admin publishes
      } catch {
        // API unreachable — defaults remain visible until connection restored
        console.warn('Theme API unreachable — using built-in defaults');
      }
    })();
  }, [isEditorPreview]);

  const updateDraftColors = useCallback((colors: Partial<ThemeColors>) => {
    setThemeState((prev) => ({ ...prev, isDirty: true, draft: { ...prev.draft, colors: { ...prev.draft.colors, ...colors } } }));
  }, []);

  const updateDraftTypography = useCallback((typography: Partial<ThemeTypography>) => {
    setThemeState((prev) => ({ ...prev, isDirty: true, draft: { ...prev.draft, typography: { ...prev.draft.typography, ...typography } } }));
  }, []);

  const updateDraftSpacing = useCallback((spacing: Partial<ThemeSpacing>) => {
    setThemeState((prev) => ({ ...prev, isDirty: true, draft: { ...prev.draft, spacing: { ...prev.draft.spacing, ...spacing } } }));
  }, []);

  const updateDraftSections = useCallback((sections: SectionConfig[]) => {
    setThemeState((prev) => ({ ...prev, isDirty: true, draft: { ...prev.draft, sections } }));
  }, []);

  const updateDraftPageContent = useCallback((pageId: string, sectionId: string, content: Partial<SectionContent>) => {
    setThemeState((prev) => {
      const pageContent = { ...(prev.draft.pageContent || {}) };
      const sectionContent = { ...(pageContent[pageId] || {}) };
      sectionContent[sectionId] = { ...(sectionContent[sectionId] || {}), ...content };
      pageContent[pageId] = sectionContent;
      return { ...prev, isDirty: true, draft: { ...prev.draft, pageContent } };
    });
  }, []);

  const switchPreset = useCallback((presetId: string) => {
    setThemeState((prev) => {
      const preset = ALL_PRESETS.find((p) => p.id === presetId);
      if (!preset) return prev;
      return { ...prev, activePresetId: presetId, draft: { ...preset }, isDirty: true };
    });
  }, []);

  // publishDraft — saves ONLY to API. No localStorage.
  const publishDraft = useCallback(async (): Promise<{ savedToServer: boolean; error?: string }> => {
    const draftToPublish: ThemePreset = JSON.parse(JSON.stringify(themeState.draft));

    try {
      const response = await themeApi.save({
        preset_id: draftToPublish.id,
        name: draftToPublish.name,
        description: draftToPublish.description,
        category: draftToPublish.category,
        business_type: draftToPublish.businessType,
        animation_style: draftToPublish.animationStyle,
        colors: draftToPublish.colors as unknown as Record<string, string>,
        typography: draftToPublish.typography as unknown as Record<string, string>,
        spacing: draftToPublish.spacing as unknown as Record<string, string>,
        sections: draftToPublish.sections.map(s => ({ id: s.id, name: s.name, visible: s.visible, order: s.order })),
        page_content: draftToPublish.pageContent as Record<string, Record<string, unknown>>,
        custom_sections: draftToPublish.customSections ? Object.fromEntries(
          Object.entries(draftToPublish.customSections).map(([pageId, sections]) => [
            pageId,
            sections.map(s => ({ id: s.id, name: s.name, visible: s.visible, order: s.order, type: s.type, blockType: s.blockType, pageId: s.pageId }))
          ])
        ) : undefined,
        is_active: true,
        is_draft: false,
      });

      if (!response.success) {
        return { savedToServer: false, error: response.message || "API error. Check server logs." };
      }

      setThemeState((prev) => {
        const draftUnchangedSincePublish = JSON.stringify(prev.draft) === JSON.stringify(draftToPublish);
        return {
          ...prev,
          published: draftToPublish,
          draft: draftUnchangedSincePublish ? draftToPublish : prev.draft,
          isDirty: draftUnchangedSincePublish ? false : prev.isDirty,
        };
      });
      applyThemeToDOM(draftToPublish);

      return { savedToServer: true };
    } catch (error) {
      return {
        savedToServer: false,
        error: error instanceof Error ? error.message : "Network error — check your connection.",
      };
    }
  }, [themeState.draft]);

  const discardDraft = useCallback(() => {
    setThemeState((prev) => ({ ...prev, draft: { ...prev.published }, isDirty: false }));
  }, []);

  const resetToDefault = useCallback((presetId: string) => {
    const defaults = ALL_PRESETS.find((p) => p.id === presetId) || LUXURY_GOLD_THEME;
    setThemeState((prev) => ({ ...prev, draft: { ...defaults }, isDirty: true }));
  }, []);

  function getDefaultContentForBlock(blockType?: string): SectionContent {
    switch (blockType) {
      case 'hero': return { heading: 'Your Heading', subheading: 'Subheading', description: 'Add your description here.', buttonText: 'Learn More' };
      case 'cta': return { heading: 'Ready to Get Started?', description: 'Take the next step today.', buttonText: 'Contact Us' };
      case 'cards': return { heading: 'Our Features', description: 'Explore what we offer', items: [{ title: 'Feature 1', description: 'Description' }, { title: 'Feature 2', description: 'Description' }, { title: 'Feature 3', description: 'Description' }] };
      case 'text': return { heading: 'Section Title', description: 'Your content goes here. Edit this text to add your own message.' };
      case 'image-text': return { heading: 'About Us', description: 'Tell your story here.', imageUrl: '', buttonText: 'Read More' };
      case 'gallery': return { heading: 'Gallery', description: 'Showcase your best work' };
      case 'testimonial': return { heading: 'What Our Clients Say', description: 'Hear from satisfied customers' };
      case 'faq': return { heading: 'Frequently Asked Questions', items: [{ title: 'Question 1?', description: 'Answer goes here.' }, { title: 'Question 2?', description: 'Answer goes here.' }] };
      case 'banner': return { heading: 'Special Announcement', description: 'Important information for visitors', buttonText: 'Learn More' };
      case 'spacer': return { heading: '' };
      default: return { heading: 'New Section', description: 'Edit this content.' };
    }
  }

  // In editor preview mode, use the override (draft from editor) instead of published
  const effectivePublished = previewOverride || themeState.published;

  const getPublishedSections = useCallback(() => {
    return [...effectivePublished.sections].sort((a, b) => a.order - b.order);
  }, [effectivePublished.sections]);

  const getPublishedPageContent = useCallback((pageId: string, sectionId: string) => {
    return effectivePublished.pageContent?.[pageId]?.[sectionId];
  }, [effectivePublished.pageContent]);

  const getDraftPageContent = useCallback((pageId: string, sectionId: string) => {
    return themeState.draft.pageContent?.[pageId]?.[sectionId];
  }, [themeState.draft.pageContent]);

  const updateDraftCustomSections = useCallback((pageId: string, sections: SectionConfig[]) => {
    setThemeState((prev) => ({
      ...prev,
      isDirty: true,
      draft: {
        ...prev.draft,
        customSections: { ...(prev.draft.customSections || {}), [pageId]: sections },
      },
    }));
  }, []);

  const addCustomSection = useCallback((pageId: string, blockType: SectionConfig['blockType'], name: string): string => {
    const sectionId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    setThemeState((prev) => {
      const existing = prev.draft.customSections?.[pageId] || [];
      const maxOrder = existing.length > 0 ? Math.max(...existing.map(s => s.order)) : 10;
      const newSection: SectionConfig = {
        id: sectionId,
        name,
        visible: true,
        order: maxOrder + 1,
        type: 'custom',
        blockType,
        pageId,
      };
      const pageContent = { ...(prev.draft.pageContent || {}) };
      const sectionContent = { ...(pageContent[pageId] || {}) };
      sectionContent[sectionId] = getDefaultContentForBlock(blockType);
      pageContent[pageId] = sectionContent;
      return {
        ...prev,
        isDirty: true,
        draft: {
          ...prev.draft,
          customSections: { ...(prev.draft.customSections || {}), [pageId]: [...existing, newSection] },
          pageContent,
        },
      };
    });
    return sectionId;
  }, []);

  const removeCustomSection = useCallback((pageId: string, sectionId: string) => {
    setThemeState((prev) => {
      const existing = prev.draft.customSections?.[pageId] || [];
      return {
        ...prev,
        isDirty: true,
        draft: {
          ...prev.draft,
          customSections: { ...(prev.draft.customSections || {}), [pageId]: existing.filter(s => s.id !== sectionId) },
        },
      };
    });
  }, []);

  // Expose effective published theme (draft override in preview, published otherwise)
  const effectiveThemeState = previewOverride
    ? { ...themeState, published: previewOverride }
    : themeState;

  return (
    <ThemeContext.Provider value={{
      themeState: effectiveThemeState, updateDraftColors, updateDraftTypography, updateDraftSpacing, updateDraftSections,
      updateDraftPageContent, updateDraftCustomSections, addCustomSection, removeCustomSection,
      switchPreset, publishDraft, discardDraft, resetToDefault,
      getPublishedSections, getPublishedPageContent, getDraftPageContent,
      isEditorPreview,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Safe fallback so the app never crashes when ThemeProvider is
// momentarily unavailable (e.g. HMR, ErrorBoundary reset).
const FALLBACK_THEME_CONTEXT: ThemeContextType = {
  themeState: getDefaultThemeState(),
  updateDraftColors: () => {},
  updateDraftTypography: () => {},
  updateDraftSpacing: () => {},
  updateDraftSections: () => {},
  updateDraftPageContent: () => {},
  updateDraftCustomSections: () => {},
  addCustomSection: () => '',
  removeCustomSection: () => {},
  switchPreset: () => {},
  publishDraft: async () => ({ savedToServer: false, error: 'Provider not mounted' }),
  discardDraft: () => {},
  resetToDefault: () => {},
  getPublishedSections: () => getDefaultThemeState().published.sections,
  getPublishedPageContent: () => undefined,
  getDraftPageContent: () => undefined,
  isEditorPreview: false,
};

export function useTheme() {
  const ctx = useContext(ThemeContext);
  return ctx ?? FALLBACK_THEME_CONTEXT;
}
